import React from 'react';
import { WebsiteDesignProps } from './types';
import { Button } from "@/components/ui/button";
import { Cpu, Terminal, Search, ChevronRight, ArrowUpRight, Grid } from 'lucide-react';

const MIT: React.FC<WebsiteDesignProps> = ({ previewData }) => {
    const schoolName = previewData?.name || "Massachusetts Institute of Technology";

    return (
        <div className="min-h-screen bg-white font-sans text-black selection:bg-[#A31F34] selection:text-white border-t-4 border-black">
            {/* Top Bar - Functional */}
            <div className="bg-[#f5f5f5] py-2 px-4 md:px-8 flex justify-between items-center text-xs font-medium text-gray-600">
                <div className="flex gap-6">
                    <a href="#" className="hover:text-black hover:underline">MIT News</a>
                    <a href="#" className="hover:text-black hover:underline">Events</a>
                    <a href="#" className="hover:text-black hover:underline">OpenCourseWare</a>
                </div>
                <div className="flex gap-4">
                    <a href="#" className="hover:text-black hover:underline">Offices & Services</a>
                    <a href="#" className="hover:text-black hover:underline">Directory</a>
                </div>
            </div>

            {/* Header w/ Search */}
            <div className="px-4 md:px-8 py-6 border-b border-gray-200">
                 <div className="max-w-[1400px] mx-auto flex flex-col md:flex-row justify-between items-end gap-6">
                     <div className="flex items-center gap-4">
                         <div className="w-16 h-16 bg-black text-white flex items-center justify-center">
                             <div className="grid grid-cols-3 gap-1 w-8 h-8">
                                 <div className="bg-[#A31F34] h-full col-span-1"></div>
                                 <div className="bg-white h-4 col-start-2"></div>
                                 <div className="bg-[#8A8B8C] h-full col-start-3"></div>
                             </div>
                         </div>
                         <h1 className="text-3xl md:text-4xl font-black tracking-tight leading-none uppercase">
                             {schoolName.split(' ').map((word, i) => (
                                 <span key={i} className="block">{word}</span>
                             ))}
                         </h1>
                     </div>
                     
                     <div className="w-full md:w-auto flex gap-4">
                         <div className="relative flex-1 md:w-64">
                             <input type="text" placeholder="Search" className="w-full border-b border-black py-2 px-0 bg-transparent text-sm font-bold placeholder:text-gray-400 focus:outline-none focus:border-[#A31F34]" />
                             <Search size={16} className="absolute right-0 top-1/2 -translate-y-1/2" />
                         </div>
                         <Button className="rounded-none bg-[#A31F34] hover:bg-black text-white font-bold uppercase text-xs px-6">
                             Give
                         </Button>
                     </div>
                 </div>
            </div>

            {/* Navigation - Minimalist */}
            <nav className="border-b border-gray-200 py-4 px-4 md:px-8 bg-white sticky top-0 z-50">
                <div className="max-w-[1400px] mx-auto flex flex-wrap gap-8 text-sm font-bold uppercase tracking-tight text-gray-800">
                    <a href="#" className="hover:text-[#A31F34]">Education</a>
                    <a href="#" className="hover:text-[#A31F34]">Research</a>
                    <a href="#" className="hover:text-[#A31F34]">Innovation</a>
                    <a href="#" className="hover:text-[#A31F34]">Admissions</a>
                    <a href="#" className="hover:text-[#A31F34]">Campus Life</a>
                    <a href="#" className="hover:text-[#A31F34]">Alumni</a>
                </div>
            </nav>

            {/* Hero Section - Daily Focus */}
            <header className="px-4 md:px-8 py-12 max-w-[1400px] mx-auto">
                 <div className="grid grid-cols-1 lg:grid-cols-12 gap-12">
                     <div className="lg:col-span-8">
                         <div className="relative aspect-video bg-gray-100 mb-6 overflow-hidden group cursor-pointer">
                             <img src="https://images.unsplash.com/photo-1581093458791-9f3c3900df4b?q=80&w=2070&auto=format&fit=crop" className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105" alt="Lab Work" />
                             <div className="absolute top-0 right-0 bg-black text-white p-2">
                                 <ArrowUpRight size={24} />
                             </div>
                         </div>
                         <h2 className="text-4xl md:text-5xl font-bold leading-tight mb-4 hover:text-[#A31F34] cursor-pointer transition-colors max-w-4xl">
                             Engineers develop new method for robust, autonomous robot navigation in complex environments.
                         </h2>
                         <p className="text-xl text-gray-600 max-w-3xl leading-relaxed">
                             The system uses a large language model to help robots understand natural language commands and navigate unfamiliar terrain without maps.
                         </p>
                     </div>

                     <div className="lg:col-span-4 flex flex-col justify-between">
                         <div className="space-y-8">
                             <div className="border-t-4 border-[#8A8B8C] pt-2">
                                 <span className="text-xs font-bold uppercase text-gray-400 mb-2 block">Spotlight</span>
                                 <h3 className="text-xl font-bold leading-snug hover:text-[#A31F34] cursor-pointer">
                                     Climate Action Plan update: Fast-tracking solutions
                                 </h3>
                             </div>
                             <div className="border-t-4 border-[#8A8B8C] pt-2">
                                 <span className="text-xs font-bold uppercase text-gray-400 mb-2 block">Community</span>
                                 <h3 className="text-xl font-bold leading-snug hover:text-[#A31F34] cursor-pointer">
                                     Celebrating Black History Month with art, music, and dialogue
                                 </h3>
                             </div>
                             <div className="border-t-4 border-[#8A8B8C] pt-2">
                                 <span className="text-xs font-bold uppercase text-gray-400 mb-2 block">Research</span>
                                 <h3 className="text-xl font-bold leading-snug hover:text-[#A31F34] cursor-pointer">
                                     Study reveals how the brain processes social interactions
                                 </h3>
                             </div>
                         </div>
                     </div>
                 </div>
            </header>

            {/* "Daily Image" / Visual Strip */}
            <div className="bg-[#121212] text-white py-20 px-4 md:px-8">
                <div className="max-w-[1400px] mx-auto text-center">
                    <Grid size={48} className="mx-auto mb-8 text-[#A31F34]" />
                    <h2 className="text-3xl md:text-5xl font-bold mb-12">"Mind and Hand"</h2>
                    
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                        <img src="https://images.unsplash.com/photo-1531482615713-2afd69097998?q=80&w=2070&auto=format&fit=crop" className="aspect-square object-cover grayscale hover:grayscale-0 transition-all duration-500" alt="Student" />
                        <img src="https://images.unsplash.com/photo-1580582932707-520aed937b7b?q=80&w=2064&auto=format&fit=crop" className="aspect-square object-cover grayscale hover:grayscale-0 transition-all duration-500" alt="Campus" />
                        <img src="https://images.unsplash.com/photo-1532094349884-543bc11b234d?q=80&w=2070&auto=format&fit=crop" className="aspect-square object-cover grayscale hover:grayscale-0 transition-all duration-500" alt="Lab" />
                        <img src="https://images.unsplash.com/photo-1526253038957-bce54e059688?q=80&w=2070&auto=format&fit=crop" className="aspect-square object-cover grayscale hover:grayscale-0 transition-all duration-500" alt="Art" />
                    </div>
                </div>
            </div>

            {/* Links Grid */}
            <section className="py-20 px-4 md:px-8 max-w-[1400px] mx-auto">
                <div className="grid grid-cols-1 md:grid-cols-3 gap-12 border-t border-black pt-12">
                     <div>
                         <h3 className="font-bold uppercase text-lg mb-6 flex items-center gap-2">
                             <Terminal size={18} /> Join Us
                         </h3>
                         <ul className="space-y-4 text-sm font-medium text-gray-600">
                             <li><a href="#" className="hover:text-[#A31F34] hover:underline flex items-center justify-between">Undergraduate <ChevronRight size={14}/></a></li>
                             <li><a href="#" className="hover:text-[#A31F34] hover:underline flex items-center justify-between">Graduate <ChevronRight size={14}/></a></li>
                             <li><a href="#" className="hover:text-[#A31F34] hover:underline flex items-center justify-between">Professional Education <ChevronRight size={14}/></a></li>
                         </ul>
                     </div>
                     <div>
                         <h3 className="font-bold uppercase text-lg mb-6 flex items-center gap-2">
                             <Cpu size={18} /> Impact
                         </h3>
                         <div className="space-y-4">
                             <p className="text-sm text-gray-600 leading-relaxed">
                                 The MIT community is driven by a shared purpose: to make a better world through education, research, and innovation.
                             </p>
                             <a href="#" className="text-sm font-bold text-[#A31F34] hover:underline">Read Impact Stories &rarr;</a>
                         </div>
                     </div>
                     <div>
                         <h3 className="font-bold uppercase text-lg mb-6">About</h3>
                         <div className="space-y-4 text-sm text-gray-600">
                             <p>77 Massachusetts Avenue <br/> Cambridge, MA 02139</p>
                             <div className="flex gap-4 pt-4">
                                 <div className="w-8 h-8 bg-black hover:bg-[#A31F34] transition-colors rounded-sm"></div>
                                 <div className="w-8 h-8 bg-black hover:bg-[#A31F34] transition-colors rounded-sm"></div>
                                 <div className="w-8 h-8 bg-black hover:bg-[#A31F34] transition-colors rounded-sm"></div>
                             </div>
                         </div>
                     </div>
                </div>
            </section>

            {/* Footer */}
            <footer className="bg-white border-t-8 border-black py-12 px-4 md:px-8">
                <div className="max-w-[1400px] mx-auto flex flex-col md:flex-row justify-between items-center text-xs font-bold uppercase text-gray-500">
                    <div className="mb-4 md:mb-0">
                        © 2026 {schoolName}
                    </div>
                    <div className="flex gap-8">
                        <a href="#" className="hover:text-black">Accessibility</a>
                        <a href="#" className="hover:text-black">Privacy Policy</a>
                        <a href="#" className="hover:text-black">Terms of Use</a>
                        <a href="#" className="hover:text-black">Jobs</a>
                    </div>
                </div>
            </footer>
        </div>
    );
};

export default MIT;
